import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { DropdownMenu } from '#pplx/frontend/aether/components/DropdownMenu/index.js';
import { Popover } from '#pplx/frontend/aether/components/Popover/index.js';
import { BoxPrimitive } from '#pplx/frontend/aether/primitives/BoxPrimitive/index.js';
import { TextPrimitive } from '#pplx/frontend/aether/primitives/TextPrimitive/index.js';
import { sidebarProjectsHeaderActionSelectedEvent, sidebarProjectsToggledEvent, } from '#pplx/frontend/apps/ask/events/sidebar/analytics.js';
import { useBrandedVisitorGate } from '#pplx/frontend/apps/ask/hooks/branded-visitor-gate-hooks/useBrandedVisitorGate.js';
import { Item } from '#pplx/frontend/apps/ask/layout/sidebar/core/lite/internal/Item.js';
import { usePersistedSidebarListCollapsed } from '#pplx/frontend/apps/ask/layout/sidebar/core/lite/internal/SectionShared.js';
import { SidebarFilter } from '#pplx/frontend/apps/ask/layout/sidebar/core/lite/internal/SidebarFilter.js';
import { AnimatedSidebarList, SidebarListHeader, SidebarListHeaderAction, } from '#pplx/frontend/apps/ask/layout/sidebar/core/lite/internal/SidebarList.js';
import { PromotedSpaces } from '#pplx/frontend/apps/ask/layout/sidebar/core/lite/internal/SpaceItems.js';
import { useSidebarProjectCreation } from '#pplx/frontend/apps/ask/layout/sidebar/SidebarProjectCreationContext.js';
import { SidebarProjectInlineCreateLoading } from '#pplx/frontend/apps/ask/layout/sidebar/SidebarProjectInlineCreateLoading.js';
import { useLocalUserSettings } from '#pplx/frontend/apps/ask/user-settings/getLocalUserSettings.js';
import { useSession } from '#pplx/frontend/auth/useSession.js';
import { ModalOrigin } from '#pplx/frontend/components/types/types.js';
import { Dynamic } from '#pplx/frontend/dynamic/Dynamic.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import { usePathname } from '#pplx/frontend/router/usePathname.js';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { defineMessage, FormattedMessage, useIntl } from 'react-intl';
const PROJECTS_SECTION_MESSAGE = defineMessage({
    defaultMessage: 'Projects',
    description: 'Section heading for the projects list in the home sidebar',
    id: '1186fda573',
});
const PINNED_PROJECTS_SECTION_MESSAGE = defineMessage({
    defaultMessage: 'Pinned Projects',
    description: 'Section heading for the pinned-only projects list in the home sidebar',
    id: 'a2f9f18221',
});
const PROJECTS_EXPAND_MESSAGE = defineMessage({
    defaultMessage: 'Expand projects',
    description: 'Accessible label for the sidebar projects section toggle when the section is collapsed',
    id: '13db384a12',
});
const PROJECTS_COLLAPSE_MESSAGE = defineMessage({
    defaultMessage: 'Collapse projects',
    description: 'Accessible label for the sidebar projects section toggle when the section is expanded',
    id: '314fb2f899',
});
const PINNED_PROJECTS_EXPAND_MESSAGE = defineMessage({
    defaultMessage: 'Expand pinned projects',
    description: 'Accessible label for the sidebar pinned-only projects section toggle when the section is collapsed',
    id: '2b89a841ba',
});
const PINNED_PROJECTS_COLLAPSE_MESSAGE = defineMessage({
    defaultMessage: 'Collapse pinned projects',
    description: 'Accessible label for the sidebar pinned-only projects section toggle when the section is expanded',
    id: 'f1ab5fb536',
});
const CREATE_PROJECT_ACTION_MESSAGE = defineMessage({
    defaultMessage: 'Create project',
    description: 'Button that creates a new project from the Projects sidebar header',
    id: '08fd2f029d',
});
const CREATE_PROJECT_PENDING_ACTION_MESSAGE = defineMessage({
    defaultMessage: 'Create project (creation in progress)',
    description: 'Disabled Projects sidebar action while another project is being created',
    id: '7fd28935ef',
});
const CREATE_PROJECT_LOGIN_TITLE_MESSAGE = defineMessage({
    defaultMessage: 'Sign in to create Projects',
    description: 'Title for the login modal opened from the Projects sidebar header',
    id: '7dd5d20b1a',
});
const CREATE_PROJECT_LOGIN_DESCRIPTION_MESSAGE = defineMessage({
    defaultMessage: 'Set instructions, organize sources, and collaborate with coworkers.',
    description: 'Description for the login modal opened from the Projects sidebar header',
    id: '5797a5b736',
});
const FILTER_PROJECTS_MESSAGE = defineMessage({
    defaultMessage: 'Filter projects',
    description: 'Accessible label for the Projects filter menu in the sidebar header',
    id: '50da88d57e',
});
const SHOW_PINNED_PROJECTS_ONLY_MESSAGE = defineMessage({
    defaultMessage: 'Show pinned projects only',
    description: 'Menu item that filters the sidebar Projects list to pinned projects',
    id: '99fce2ddeb',
});
const SHOW_ALL_PROJECTS_MESSAGE = defineMessage({
    defaultMessage: 'Show all projects',
    description: 'Menu item that removes the pinned-only filter from the sidebar Projects list',
    id: '0ec3ddc3cf',
});
const PROJECTS_HREF = '/projects';
const LazyProjectInlineCreateRow = Dynamic(async () => ({
    default: (await import('#pplx/frontend/apps/ask/layout/sidebar/ProjectInlineCreateRow.js'))
        .ProjectInlineCreateRow,
}), { loading: SidebarProjectInlineCreateLoading });
const ProjectsHeaderActions = ({ disabled, onCreate, focusRequestId, showPinnedOnly, onPinnedOnlyChange, placement }) => {
    const { $t } = useIntl();
    const { track } = useInstrumentation();
    const createProjectButtonRef = useRef(null);
    const createProjectLabel = $t(disabled ? CREATE_PROJECT_PENDING_ACTION_MESSAGE : CREATE_PROJECT_ACTION_MESSAGE);
    const filterProjectsLabel = $t(FILTER_PROJECTS_MESSAGE);
    useEffect(() => {
        if (focusRequestId > 0)
            createProjectButtonRef.current?.focus();
    }, [focusRequestId]);
    const handleCreateProject = useCallback(() => {
        track(sidebarProjectsHeaderActionSelectedEvent({
            name: 'sidebar projects header action selected',
            data: { action: 'createProject', placement },
        }));
        onCreate('sidebarHeader');
    }, [onCreate, placement, track]);
    const handleProjectFilterChange = useCallback((filter) => {
        const enabled = filter === 'pinned';
        if (enabled === showPinnedOnly)
            return;
        track(sidebarProjectsHeaderActionSelectedEvent({
            name: 'sidebar projects header action selected',
            data: { action: 'showPinnedOnly', enabled, placement },
        }));
        onPinnedOnlyChange(enabled);
    }, [onPinnedOnlyChange, placement, showPinnedOnly, track]);
    return (_jsxs(_Fragment, { children: [_jsx(SidebarListHeaderAction, { ref: createProjectButtonRef, label: createProjectLabel, icon: $icon('plus'), disabled: disabled, "aria-busy": disabled, onClick: handleCreateProject }), _jsx("div", { className: "hidden md:block", children: _jsxs(SidebarFilter, { label: filterProjectsLabel, value: showPinnedOnly ? 'pinned' : 'all', onValueChange: handleProjectFilterChange, minWidthPx: 220, children: [_jsx(DropdownMenu.RadioItem, { value: "pinned", children: _jsx(FormattedMessage, { ...SHOW_PINNED_PROJECTS_ONLY_MESSAGE }) }), _jsx(DropdownMenu.RadioItem, { value: "all", children: _jsx(FormattedMessage, { ...SHOW_ALL_PROJECTS_MESSAGE }) })] }) })] }));
};
const SidebarProjectsWithInlineCreation = (props) => {
    const { editor, anchoredProjects, startInlineCreate } = useSidebarProjectCreation();
    return (_jsx(SidebarProjectsContent, { ...props, projectCreate: {
            ...props.projectCreate,
            controller: { editor, anchoredProjects, startInlineCreate },
        } }));
};
export const SidebarProjects = ({ projectCreate, ...props }) => {
    if (projectCreate.kind === 'inline') {
        return _jsx(SidebarProjectsWithInlineCreation, { ...props, projectCreate: projectCreate });
    }
    return _jsx(SidebarProjectsContent, { ...props, projectCreate: projectCreate });
};
const SidebarProjectsContent = ({ isSidebarOpen, sidebarProgress, collapsible = false, projectCreate, }) => {
    const { $t } = useIntl();
    const { track } = useInstrumentation();
    const pathname = usePathname();
    const { status: sessionStatus } = useSession();
    const { openVisitorLoginUpsell } = useBrandedVisitorGate({
        enabled: sessionStatus === 'unauthenticated',
    });
    const [isProjectsCollapsed, toggleProjectsCollapsed] = usePersistedSidebarListCollapsed('isSidebarSpacesCollapsed');
    const [isProjectsLoaded, setIsProjectsLoaded] = useState(false);
    const [shouldExpandProjects, setShouldExpandProjects] = useState(false);
    const [showPinnedOnly, setShowPinnedOnly] = useLocalUserSettings('sidebarShowPinnedProjectsOnly', false);
    const projectCreationController = projectCreate.kind === 'inline' ? projectCreate.controller : null;
    const editor = projectCreationController?.editor;
    const inlineCreate = editor?.draft;
    const anchoredProjects = projectCreationController?.anchoredProjects;
    const [headerCreateFocusRequestId, setHeaderCreateFocusRequestId] = useState(0);
    const label = $t(showPinnedOnly ? PINNED_PROJECTS_SECTION_MESSAGE : PROJECTS_SECTION_MESSAGE);
    const toggleLabels = showPinnedOnly
        ? {
            expand: $t(PINNED_PROJECTS_EXPAND_MESSAGE),
            collapse: $t(PINNED_PROJECTS_COLLAPSE_MESSAGE),
        }
        : { expand: $t(PROJECTS_EXPAND_MESSAGE), collapse: $t(PROJECTS_COLLAPSE_MESSAGE) };
    const handleProjectListLoaded = useCallback(() => setIsProjectsLoaded(true), []);
    const trackProjectsToggled = useCallback((expanded) => {
        track(sidebarProjectsToggledEvent({
            name: 'sidebar projects toggled',
            data: { expanded, source: 'computerInbox' },
        }));
    }, [track]);
    const handleInlineCreateProjectAuthenticationUnavailable = useCallback(() => {
        editor?.dismiss();
        if (sessionStatus !== 'unauthenticated')
            return;
        openVisitorLoginUpsell({
            title: $t(CREATE_PROJECT_LOGIN_TITLE_MESSAGE),
            description: $t(CREATE_PROJECT_LOGIN_DESCRIPTION_MESSAGE),
            origin: ModalOrigin.SPACES_LANDING,
        });
    }, [$t, editor, openVisitorLoginUpsell, sessionStatus]);
    const handleToggleProjectsCollapsed = useCallback(() => {
        if (!isProjectsCollapsed)
            editor?.clearFocusRequest();
        if (isProjectsCollapsed && !isProjectsLoaded) {
            setShouldExpandProjects(true);
            return;
        }
        toggleProjectsCollapsed();
        trackProjectsToggled(isProjectsCollapsed);
    }, [
        editor,
        isProjectsCollapsed,
        isProjectsLoaded,
        toggleProjectsCollapsed,
        trackProjectsToggled,
    ]);
    const handleStartInlineCreateProject = useCallback((entryPoint) => {
        if (sessionStatus !== 'authenticated') {
            handleInlineCreateProjectAuthenticationUnavailable();
            return;
        }
        if (projectCreate.kind === 'modal') {
            if (projectCreate.disabled)
                return;
            projectCreate.open({ entryPoint });
            return;
        }
        if (collapsible && isProjectsCollapsed) {
            if (isProjectsLoaded) {
                toggleProjectsCollapsed();
                trackProjectsToggled(true);
            }
            else {
                setShouldExpandProjects(true);
            }
        }
        projectCreate.controller.startInlineCreate(entryPoint);
    }, [
        collapsible,
        isProjectsCollapsed,
        isProjectsLoaded,
        handleInlineCreateProjectAuthenticationUnavailable,
        projectCreate,
        sessionStatus,
        toggleProjectsCollapsed,
        trackProjectsToggled,
    ]);
    const handleCancelInlineCreateProject = useCallback(() => {
        setHeaderCreateFocusRequestId((requestId) => requestId + 1);
    }, []);
    useEffect(() => {
        if (!shouldExpandProjects || !isProjectsLoaded || !isProjectsCollapsed)
            return;
        toggleProjectsCollapsed();
        trackProjectsToggled(true);
        setShouldExpandProjects(false);
        if (inlineCreate && projectCreate.kind === 'inline') {
            projectCreate.controller.startInlineCreate(inlineCreate.entryPoint);
        }
    }, [
        isProjectsCollapsed,
        isProjectsLoaded,
        shouldExpandProjects,
        inlineCreate,
        projectCreate,
        toggleProjectsCollapsed,
        trackProjectsToggled,
    ]);
    const inlineCreateRow = sessionStatus === 'authenticated' && projectCreate.kind === 'inline' && editor ? (_jsx(LazyProjectInlineCreateRow, { editor: editor, onAuthenticationUnavailable: handleInlineCreateProjectAuthenticationUnavailable, onCancel: handleCancelInlineCreateProject })) : null;
    const isCreateDisabled = projectCreate.disabled;
    if (!isSidebarOpen) {
        return (_jsx("div", { className: "px-2 w-full", children: _jsx(Popover, { interaction: "hover", side: "right", align: "start", openDelayMs: 200, closeDelayMs: 200, triggerElement: _jsx("div", { children: _jsx(Item, { variant: "primary", href: PROJECTS_HREF, text: label, icon: $icon('folders'), sidebarOpen: isSidebarOpen, sidebarProgress: sidebarProgress, selected: pathname?.startsWith(PROJECTS_HREF) ?? false, collapsed: true, foreground: true }) }), children: _jsxs("div", { className: "flex flex-col", children: [_jsx(TextPrimitive, { color: "light", size: "xs", truncate: true, className: "px-3 py-2", children: label }), _jsx("div", { className: "flex flex-col gap-px", children: _jsx(PromotedSpaces, { isSidebarOpen: isSidebarOpen, showPinnedOnly: showPinnedOnly, anchoredProjects: anchoredProjects, isInPopover: true, collapsed: true, defaultSpaceExpanded: false, useContainerGap: true }) })] }) }) }));
    }
    if (!collapsible) {
        return (_jsxs(BoxPrimitive, { className: "mt-4 pt-1 pb-2 w-full px-2", children: [_jsx(SidebarListHeader, { label: label, href: PROJECTS_HREF, actionElement: _jsx(ProjectsHeaderActions, { disabled: isCreateDisabled, onCreate: handleStartInlineCreateProject, focusRequestId: headerCreateFocusRequestId, showPinnedOnly: showPinnedOnly, onPinnedOnlyChange: setShowPinnedOnly, placement: "sidebar" }) }), _jsx("div", { className: "mt-px flex flex-col gap-px", children: _jsx(PromotedSpaces, { isSidebarOpen: isSidebarOpen, showPinnedOnly: showPinnedOnly, collapsed: false, defaultSpaceExpanded: false, useContainerGap: true, inlineCreate: inlineCreateRow ? { row: inlineCreateRow } : undefined, anchoredProjects: anchoredProjects }) })] }));
    }
    return (_jsxs(_Fragment, { children: [_jsx(SidebarListHeader, { label: label, toggleLabels: toggleLabels, href: PROJECTS_HREF, actionElement: _jsx(ProjectsHeaderActions, { disabled: isCreateDisabled, onCreate: handleStartInlineCreateProject, focusRequestId: headerCreateFocusRequestId, showPinnedOnly: showPinnedOnly, onPinnedOnlyChange: setShowPinnedOnly, placement: "computerInbox" }), collapsed: isProjectsCollapsed, onToggleCollapsed: handleToggleProjectsCollapsed, variant: "computerInbox" }), _jsx(AnimatedSidebarList, { collapsed: isProjectsCollapsed, children: _jsx(PromotedSpaces, { isSidebarOpen: isSidebarOpen, showPinnedOnly: showPinnedOnly, collapsed: isProjectsCollapsed, defaultSpaceExpanded: false, useContainerGap: true, foreground: true, onProjectListLoaded: handleProjectListLoaded, emptyStateVariant: "computerInbox", inlineCreate: inlineCreateRow ? { row: inlineCreateRow } : undefined, anchoredProjects: anchoredProjects }) })] }));
};
//# sourceMappingURL=SidebarProjects.js.map